import { optionalQueryParams } from "./apiEndpointHelpers";
import type { ApiEndpointBuilder } from "./apiEndpointHelpers";
import { buildAdminV2CrmActionScopeEndpoints } from "./endpoints/adminV2CrmActionScopeEndpoints";
import { buildAdminV2CrmPeopleBulkHealthEndpoints } from "./endpoints/adminV2CrmPeopleBulkHealthEndpoints";
import { buildAdminV2CrmRecordNoteEndpoints } from "./endpoints/adminV2CrmRecordNoteEndpoints";
import { buildAdminV2CrmSignInSplitEndpoints } from "./endpoints/adminV2CrmSignInSplitEndpoints";
import { buildAdminV2CrmStateCountEndpoints } from "./endpoints/adminV2CrmStateCountEndpoints";
import { buildAdminV2CrmTagCatalogueEndpoints } from "./endpoints/adminV2CrmTagCatalogueEndpoints";
import { buildAdminV2CrmUserAccountAccessEndpoints } from "./endpoints/adminV2CrmUserAccountAccessEndpoints";

import type {
  AdminV2CrmFilterSuggestionsResponse,
  AdminV2CrmOrganisationContactCandidatesResponse,
  AdminV2CrmOrganisationRecordResponse,
  AdminV2CrmUserInternalNoteResponse,
  AdminV2CrmUserInternalNotesResponse,
  AdminV2CrmUserRecordResponse,
  AdminV2CrmUserRelationshipRecordResponse,
  AdminV2CrmUsersListResponse,
  AdminV2CrmUserStatusUpdateResponse,
} from "@/types/adminV2";
import type {
  AdminV2CrmUserContactDetailsRequest,
  AdminV2CrmUserContactDetailsResponse,
} from "@/types/adminV2/crmUserContactDetails";

/**
 * Admin V2 CRM user and organisation-relationship endpoint definitions.
 *
 * @remarks
 * Keeps generated hook names and cache tags on the shared apiSlice while
 * moving the Sprint 2 CRM users relationship workflow out of baseApi.ts.
 */
export const buildAdminV2CrmUserEndpoints = (builder: ApiEndpointBuilder) => ({
  ...buildAdminV2CrmActionScopeEndpoints(builder),
  ...buildAdminV2CrmUserAccountAccessEndpoints(builder),
  ...buildAdminV2CrmTagCatalogueEndpoints(builder),
  ...buildAdminV2CrmRecordNoteEndpoints(builder),
  ...buildAdminV2CrmSignInSplitEndpoints(builder),
  ...buildAdminV2CrmPeopleBulkHealthEndpoints(builder),
  ...buildAdminV2CrmStateCountEndpoints(builder),

  getAdminV2CrmUsers: builder.query<
    AdminV2CrmUsersListResponse,
    {
      filters?: string;
      matchLogic?: "all" | "any";
      scopeFilters?: string;
      scopeFilterMatchLogic?: "all" | "any";
      q?: string;
      /** Users dashboard only: search what the row shows (row 29). */
      searchScope?: "row";
      /** TAG-03: tag clauses also see the linked contact's tags. */
      tagView?: "linked_pair";
      organisationId?: string;
      limit?: number;
      offset?: number;
      sortBy?: "name" | "email" | "last_meaningful_activity_at" | "created_at";
      sortDirection?: "asc" | "desc";
      /** M53: each user's own `recordTags` on this page (Users row 72). */
      includeRecordTags?: true;
      /** M54: `communicationEmailDiffers` on this page (Users row 58). */
      includeCommunicationEmailDiffers?: true;
      /** SEC-05: `communicationEmail` for the row 58 tooltip, when allowed. */
      includeCommunicationEmail?: true;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/crm/users",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2CrmUsers", id: "LIST" }],
    // Support workflows can update contact roles and list badges in the
    // same session, so keep the CRM users cache shorter than passive
    // reference data and rely on mutation invalidation.
    keepUnusedDataFor: 20,
  }),

  /** One user for the record page, in the users list scope and projection. */
  getAdminV2CrmUserRecord: builder.query<
    AdminV2CrmUserRecordResponse,
    /** `linked_pair`: recordTags also carry the linked contact's (TAG-03). */
    { userId: string; tagView?: "linked_pair"; readOwner?: string }
  >({
    query: ({ userId, tagView }) => ({
      url: `/admin/v2/crm/users/${encodeURIComponent(userId)}`,
      ...(tagView ? { params: { tagView } } : {}),
    }),
    // LIST keeps the record in step with the status and note mutations,
    // which already invalidate the users list.
    providesTags: (_result, _error, { userId }) => [
      { type: "AdminV2CrmUsers", id: "LIST" },
      { type: "AdminV2CrmUsers", id: `record:${userId}` },
    ],
    keepUnusedDataFor: 20,
  }),

  updateAdminV2CrmUserContactDetails: builder.mutation<
    AdminV2CrmUserContactDetailsResponse,
    { userId: string; body: AdminV2CrmUserContactDetailsRequest }
  >({
    query: ({ userId, body }) => ({
      url: `/admin/v2/crm/users/${encodeURIComponent(userId)}/contact-details`,
      method: "PUT",
      data: body,
    }),
    // The linked contact owns these values. Refresh both record/list views
    // and their Activity rather than leaving an old email on another tab.
    invalidatesTags: [
      "AdminV2CrmUsers",
      "AdminV2CrmPeople",
      "AdminV2CrmTimeline",
      "AdminV2AuditLog",
    ],
  }),

  getAdminV2CrmUsersFilterSuggestions: builder.query<
    AdminV2CrmFilterSuggestionsResponse,
    {
      selectedOwnerIds?: string[];
      selectedTagIds?: string[];
      /** Sent in the body with the draft (TAG-03 linked-pair tags). */
      tagView?: "linked_pair";
      search?: string;
      filters?: Array<{
        category: string;
        field: string;
        condition: string;
        value: string | string[];
      }>;
      activeClause: {
        category: string;
        field: string;
        condition: string;
        query: string;
      };
      limit?: number;
      organisationId?: string;
      /** Only the Users dashboard drawer sends `row`. */
      searchScope?: "row";
    }
  >({
    query: ({ organisationId, searchScope, ...body }) => ({
      url: "/admin/v2/crm/users/filter-suggestions",
      method: "POST",
      data: body,
      params: optionalQueryParams({ organisationId, searchScope }),
    }),
    keepUnusedDataFor: 20,
  }),

  requestAdminV2CrmUserStatusChange: builder.mutation<
    { ok: true },
    {
      userId: string;
      body: {
        organisationId: string;
        requestedStatus:
          "organisation_user" | "partner_user" | "partner_client_user";
        reason: string;
      };
    }
  >({
    query: ({ userId, body }) => ({
      url: `/admin/v2/crm/users/${userId}/status-requests`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { userId, body }) => [
      {
        type: "AdminV2CrmTimeline",
        id: `app_user:${userId}:${body.organisationId}`,
      },
      { type: "AdminV2CrmTimeline", id: `app_user:${userId}:all` },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2CrmUserStatus: builder.mutation<
    AdminV2CrmUserStatusUpdateResponse,
    {
      userId: string;
      body: {
        organisationId: string;
        status: null;
        reason: string;
      };
    }
  >({
    query: ({ userId, body }) => ({
      url: `/admin/v2/crm/users/${userId}/status`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { userId, body }) => [
      { type: "AdminV2CrmUsers", id: "LIST" },
      {
        type: "AdminV2CrmUsers",
        id: `${userId}:${body.organisationId}`,
      },
      {
        type: "AdminV2CrmTimeline",
        id: `app_user:${userId}:${body.organisationId}`,
      },
      {
        type: "AdminV2CrmTimeline",
        id: `app_user:${userId}:all`,
      },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  createAdminV2CrmUserInternalNote: builder.mutation<
    AdminV2CrmUserInternalNoteResponse,
    {
      userId: string;
      body: {
        organisationId: string;
        note: string;
        /**
         * One UUID v4 per draft, reused on retry: the API returns the
         * existing note for the same author, user, organisation and id.
         */
        clientRequestId?: string;
      };
    }
  >({
    query: ({ userId, body }) => ({
      url: `/admin/v2/crm/users/${userId}/internal-notes`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { userId, body }) => [
      {
        type: "AdminV2CrmTimeline",
        id: `app_user:${userId}:${body.organisationId}`,
      },
      {
        type: "AdminV2CrmTimeline",
        id: `app_user:${userId}:all`,
      },
      {
        type: "AdminV2CrmUserInternalNotes",
        id: `${userId}:${body.organisationId}`,
      },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2CrmUserInternalNotes: builder.query<
    AdminV2CrmUserInternalNotesResponse,
    {
      userId: string;
      organisationId?: string;
      limit?: number;
    }
  >({
    query: ({ userId, organisationId, limit }) => ({
      url: `/admin/v2/crm/users/${userId}/internal-notes`,
      params: {
        ...(organisationId ? { organisationId } : {}),
        ...(limit ? { limit } : {}),
      },
    }),
    providesTags: (_result, _error, { userId, organisationId }) => [
      { type: "AdminV2CrmUserInternalNotes", id: `USER:${userId}` },
      {
        type: "AdminV2CrmUserInternalNotes",
        id: `${userId}:${organisationId ?? "GLOBAL"}`,
      },
    ],
    keepUnusedDataFor: 10,
  }),

  getAdminV2CrmUserRelationshipRecord: builder.query<
    AdminV2CrmUserRelationshipRecordResponse,
    {
      userId: string;
      organisationId: string;
    }
  >({
    query: ({ organisationId, userId }) => ({
      url: `/admin/v2/crm/users/${userId}/relationships/${organisationId}`,
    }),
    providesTags: (_result, _error, { organisationId, userId }) => [
      {
        type: "AdminV2CrmUsers",
        id: `${userId}:${organisationId}`,
      },
    ],
    keepUnusedDataFor: 20,
  }),

  getAdminV2CrmOrganisationRecord: builder.query<
    AdminV2CrmOrganisationRecordResponse,
    { organisationId: string; readOwner?: string }
  >({
    query: ({ organisationId }) => ({
      url: `/admin/v2/crm/organisations/${organisationId}`,
    }),
    providesTags: (_result, _error, { organisationId }) => [
      {
        type: "AdminV2CrmUsers",
        id: `organisation:${organisationId}`,
      },
    ],
    keepUnusedDataFor: 20,
  }),

  getAdminV2CrmOrganisationContactCandidates: builder.query<
    AdminV2CrmOrganisationContactCandidatesResponse,
    {
      organisationId: string;
      q?: string;
      limit?: number;
    }
  >({
    query: ({ organisationId, ...params }) => ({
      url: `/admin/v2/crm/organisations/${organisationId}/contact-candidates`,
      params: optionalQueryParams(params),
    }),
    providesTags: (_result, _error, { organisationId }) => [
      {
        type: "AdminV2CrmUsers",
        id: `contact-candidates:${organisationId}`,
      },
    ],
    keepUnusedDataFor: 10,
  }),

  updateAdminV2CrmOrganisationContactRoles: builder.mutation<
    AdminV2CrmOrganisationRecordResponse,
    {
      organisationId: string;
      body: {
        primaryContactPersonId?: string | null;
        billingContactPersonId?: string | null;
      };
    }
  >({
    query: ({ organisationId, body }) => ({
      url: `/admin/v2/crm/organisations/${organisationId}/contact-roles`,
      method: "PUT",
      data: body,
    }),
    // Contact-role edits affect the users list badges, the org summary,
    // and the candidate warnings in one drilldown flow, so invalidate
    // the whole Sprint 2 surface together.
    invalidatesTags: (_result, _error, { organisationId }) => [
      { type: "AdminV2CrmUsers", id: "LIST" },
      {
        type: "AdminV2CrmUsers",
        id: `organisation:${organisationId}`,
      },
      {
        type: "AdminV2CrmUsers",
        id: `contact-candidates:${organisationId}`,
      },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
